import styled, { keyframes } from 'styled-components';
import { detectReferencePoint } from '../../../functions/launcherElementFunctions/detectReferencePoint';
import { getElementById } from '../../../functions/launcherElementFunctions/getElementById';
import { updateObjectNestedProperties } from '../../../functions/objectFunctions/updateObjectNestedProperties';
import { useScene } from '../../../hooks';
import { useElements } from '../../../hooks/useElements';
import { LauncherElement } from '../../../types/launcherElement';

export const ReferecePoint = () => {
  const scene = useScene();
  const lastSelectedElementID = scene.selectedElementIDs.slice(-1)[0];
  const [selectedElement, setSelectedElement] = useElements<LauncherElement>({
    id: lastSelectedElementID,
  });
  const [elements] = useElements<LauncherElement[]>();
  const point = detectReferencePoint(selectedElement);

  const setPoint = (pointName: string) => {
    let [element, parent] = getElementById(elements, lastSelectedElementID);
    if (!element) return;

    const elementDiv = document.getElementById(lastSelectedElementID);
    const parentDiv = document.getElementById(parent?.id || 'launcherWindow');
    const elementRect = elementDiv?.getBoundingClientRect()!;
    const parentRect = parentDiv?.getBoundingClientRect()!;

    const elementDim = {
      top: (elementRect?.top || 0) - (parentRect?.top || 0),
      left: (elementRect?.left || 0) - (parentRect?.left || 0),
      bottom: (parentRect?.bottom || 0) - (elementRect?.bottom || 0),
      right: (parentRect?.right || 0) - (elementRect?.right || 0),
    };

    element = updateObjectNestedProperties(element, {
      'props.class.position': 'relative',
      'props.class.top': undefined,
      'props.class.bottom': undefined,
      'props.class.left': undefined,
      'props.class.right': undefined,
    });

    switch (pointName) {
      case 'topLeft':
        element = updateObjectNestedProperties(element, {
          'props.class.position': 'absolute',
          'props.class.left': elementDim?.left || 0,
          'props.class.top': elementDim?.top || 0,
        });
        break;
      case 'topRight':
        element = updateObjectNestedProperties(element, {
          'props.class.position': 'absolute',
          'props.class.right': elementDim?.right || 0,
          'props.class.top': elementDim?.top || 0,
        });
        break;
      case 'bottomLeft':
        element = updateObjectNestedProperties(element, {
          'props.class.position': 'absolute',
          'props.class.left': elementDim?.left || 0,
          'props.class.bottom': elementDim?.bottom || 0,
        });
        break;
      case 'bottomRight':
        element = updateObjectNestedProperties(element, {
          'props.class.position': 'absolute',
          'props.class.right': elementDim?.right || 0,
          'props.class.bottom': elementDim?.bottom || 0,
        });
        break;
    }

    if (element) setSelectedElement(element);
  };

  const drawElements = () => {
    switch (point) {
      case 'topLeft':
        return (
          <>
            <Label $top key="label-x-topLeft">
              x
            </Label>
            <Label $left key="label-y-topLeft">
              y
            </Label>
            <XArrow $topLeft key="arrow-x-topLeft" />
            <YArrow $topLeft key="arrow-y-topLeft" />
          </>
        );
      case 'topRight':
        return (
          <>
            <Label $top key="label-x-topRight">
              x
            </Label>
            <Label $right key="label-y-topRight">
              y
            </Label>
            <XArrow $topRight key="arrow-x-topRight" />
            <YArrow $topRight key="arrow-y-topRight" />
          </>
        );
      case 'bottomLeft':
        return (
          <>
            <Label $bottom key="label-x-bottomLeft">
              x
            </Label>
            <Label $left key="label-y-bottomLeft">
              y
            </Label>
            <XArrow $bottomLeft key="arrow-x-bottomLeft" />
            <YArrow $bottomLeft key="arrow-y-bottomLeft" />
          </>
        );
      case 'bottomRight':
        return (
          <>
            <Label $bottom key="label-x-bottomRight">
              x
            </Label>
            <Label $right key="label-y-bottomRight">
              y
            </Label>
            <XArrow $bottomRight key="arrow-x-bottomRight" />
            <YArrow $bottomRight key="arrow-y-bottomRight" />
          </>
        );
    }

    return <></>;
  };

  return (
    <Container>
      <Box>
        {drawElements()}

        {point !== 'relative' && (
          <Circle onClick={() => setPoint('relative')} />
        )}
        {point === 'relative' && <span key="relative">Relative</span>}

        <Circle
          $dimmed={point === 'relative'}
          $topLeft
          onClick={() => setPoint('topLeft')}
        />
        <Circle
          $dimmed={point === 'relative'}
          $topRight
          onClick={() => setPoint('topRight')}
        />
        <Circle
          $dimmed={point === 'relative'}
          $bottomLeft
          onClick={() => setPoint('bottomLeft')}
        />
        <Circle
          $dimmed={point === 'relative'}
          $bottomRight
          onClick={() => setPoint('bottomRight')}
        />
      </Box>
    </Container>
  );
};

const Container = styled.div`
  width: 100px;
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Box = styled.div`
  position: relative;
  width: 70px;
  height: 70px;
  border: 1px solid var(--input-border-color);

  display: flex;
  justify-content: center;
  align-items: center;

  font-size: 12px;
  font-weight: 300;
  color: var(--accent-color);
`;

const Circle = styled.div<{
  $dimmed?: boolean;
  $topLeft?: boolean;
  $topRight?: boolean;
  $bottomLeft?: boolean;
  $bottomRight?: boolean;
}>`
  position: absolute;
  width: 20px;
  height: 20px;

  border: 1px solid var(--input-border-color);
  border-radius: 50%;
  background-color: var(--panel-bg-color);

  transition:
    border 0.3s ease,
    background-color 0.3s ease;

  &:hover {
    border: 1px solid var(--text-color);
    background-color: var(--panel-bg-color-hover);
  }

  ${({ $dimmed }) => $dimmed && `background-color: transparent;`}

  ${({ $topLeft }) =>
    $topLeft && `top: 0; left: 0; transform: translate(-50%, -50%);`}

  ${({ $topRight }) =>
    $topRight && `top: 0; right: 0; transform: translate(50%, -50%);`}

  ${({ $bottomLeft }) =>
    $bottomLeft && `bottom: 0; left: 0; transform: translate(-50%, 50%);`}

  ${({ $bottomRight }) =>
    $bottomRight && `bottom: 0; right: 0; transform: translate(50%, 50%);`}
`;

const arrowWidthBounce = keyframes`
  0% {
    width: 0%;
  }
  50% {
    width: 60%;
  }
  90% {
    width: 62%;
  }
  100% {
    width: 60%;
  }
`;
const arrowHeightBounce = keyframes`
  0% {
    height: 0%;
  }
  50% {
    height: 60%;
  }
  90% {
    height: 62%;
  }
  100% {
    height: 60%;
  }
`;

const XArrow = styled.div<{
  $topLeft?: boolean;
  $topRight?: boolean;
  $bottomLeft?: boolean;
  $bottomRight?: boolean;
}>`
  animation: ${arrowWidthBounce} 0.3s cubic-bezier(0.075, 0.82, 0.165, 1);
  position: absolute;
  width: 60%;
  height: 1px;

  background-color: var(--accent-color);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 5px;
    height: 1px;
    background-color: var(--accent-color);

    transform: translateY(1px) rotate(-30deg);
  }
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 5px;
    height: 1px;
    background-color: var(--accent-color);

    transform: translateY(-1px) rotate(30deg);
  }

  ${({ $topLeft }) => $topLeft && `top: 0; left: 0;`}

  ${({ $topRight }) =>
    $topRight && `top: 0; right: 0; transform: rotate(180deg);`}

  ${({ $bottomLeft }) => $bottomLeft && `bottom: 0; left: 0;`}

  ${({ $bottomRight }) =>
    $bottomRight && `bottom: 0; right: 0; transform: rotate(180deg);`}
`;

const YArrow = styled.div<{
  $topLeft?: boolean;
  $topRight?: boolean;
  $bottomLeft?: boolean;
  $bottomRight?: boolean;
}>`
  animation: ${arrowHeightBounce} 0.3s cubic-bezier(0.075, 0.82, 0.165, 1);
  position: absolute;
  width: 1px;
  height: 60%;

  background-color: var(--accent-color);

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 5px;
    height: 1px;
    background-color: var(--accent-color);

    transform: translate(-1px, -1px) rotate(-60deg);
  }
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: 0;
    width: 5px;
    height: 1px;
    background-color: var(--accent-color);

    transform: translate(1px, -1px) rotate(60deg);
  }

  ${({ $topLeft }) => $topLeft && `top: 0; left: 0;`}

  ${({ $topRight }) => $topRight && `top: 0; right: 0;`}

  ${({ $bottomLeft }) =>
    $bottomLeft && `bottom: 0; left: 0; transform: rotate(180deg);`}

  ${({ $bottomRight }) =>
    $bottomRight && `bottom: 0; right: 0; transform: rotate(180deg);`}
`;

const Label = styled.div<{
  $top?: boolean;
  $bottom?: boolean;
  $right?: boolean;
  $left?: boolean;
}>`
  animation: fadeIn 0.5s ease;
  position: absolute;
  padding: 5px;

  display: flex;
  justify-content: center;
  align-items: center;

  color: var(--accent-color);

  ${({ $top }) => $top && `top: 0; width: 100%; transform: translateY(-100%)`}

  ${({ $bottom }) =>
    $bottom && `bottom: 0; width: 100%; transform: translateY(100%)`}
  
  ${({ $left }) =>
    $left && `left: 0; height: 100%; transform: translateX(-100%)`}

  ${({ $right }) =>
    $right && `right: 0; height: 100%; transform: translateX(100%)`}
`;
